package com.wintermar.inspection

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Boolean
import kotlin.Long
import kotlin.String
import migrations.TemplatePageSectionQuestionTable

public class TemplatePageSectionQuestionTableQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> getTemplatePageSectionQuestionsBySectionId(sectionId: String, mapper: (
    id: String,
    sectionId: String,
    questionText: String,
    guidelines: String?,
    required: Boolean?,
    orderPosition: Long,
    answerType: String,
    failedOptionId: String?,
    neutralOptionId: String?,
  ) -> T): Query<T> = GetTemplatePageSectionQuestionsBySectionIdQuery(sectionId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getBoolean(4),
      cursor.getLong(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8)
    )
  }

  public fun getTemplatePageSectionQuestionsBySectionId(sectionId: String): Query<TemplatePageSectionQuestionTable> = getTemplatePageSectionQuestionsBySectionId(sectionId, ::TemplatePageSectionQuestionTable)

  public fun <T : Any> getTemplatePageSectionQuestionById(id: String, mapper: (
    id: String,
    sectionId: String,
    questionText: String,
    guidelines: String?,
    required: Boolean?,
    orderPosition: Long,
    answerType: String,
    failedOptionId: String?,
    neutralOptionId: String?,
  ) -> T): Query<T> = GetTemplatePageSectionQuestionByIdQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getBoolean(4),
      cursor.getLong(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8)
    )
  }

  public fun getTemplatePageSectionQuestionById(id: String): Query<TemplatePageSectionQuestionTable> = getTemplatePageSectionQuestionById(id, ::TemplatePageSectionQuestionTable)

  public fun getTemplatePageSectionQuestionCountBySectionId(sectionId: String): Query<Long> = GetTemplatePageSectionQuestionCountBySectionIdQuery(sectionId) { cursor ->
    cursor.getLong(0)!!
  }

  /**
   * @return The number of rows updated.
   */
  public fun insertTemplatePageSectionQuestion(
    id: String,
    sectionId: String,
    questionText: String,
    guidelines: String?,
    required: Boolean?,
    orderPosition: Long,
    answerType: String,
    failedOptionId: String?,
    neutralOptionId: String?,
  ): QueryResult<Long> {
    val result = driver.execute(-1_522_992_149, """
        |INSERT INTO TemplatePageSectionQuestionTable(id, sectionId, questionText, guidelines, required, orderPosition, answerType, failedOptionId, neutralOptionId)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(id) DO UPDATE SET
        |   sectionId=excluded.sectionId,
        |   questionText=excluded.questionText,
        |   guidelines=excluded.guidelines,
        |   required=excluded.required,
        |   orderPosition=excluded.orderPosition,
        |   answerType=excluded.answerType,
        |   failedOptionId=excluded.failedOptionId,
        |   neutralOptionId=excluded.neutralOptionId
        """.trimMargin(), 9) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, sectionId)
          bindString(parameterIndex++, questionText)
          bindString(parameterIndex++, guidelines)
          bindBoolean(parameterIndex++, required)
          bindLong(parameterIndex++, orderPosition)
          bindString(parameterIndex++, answerType)
          bindString(parameterIndex++, failedOptionId)
          bindString(parameterIndex++, neutralOptionId)
        }
    notifyQueries(-1_522_992_149) { emit ->
      emit("TemplatePageSectionQuestionTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun insertReplaceTemplatePageSectionQuestion(
    id: String,
    sectionId: String,
    questionText: String,
    guidelines: String?,
    required: Boolean?,
    orderPosition: Long,
    answerType: String,
    failedOptionId: String?,
    neutralOptionId: String?,
  ): QueryResult<Long> {
    val result = driver.execute(362_011_045, """
        |INSERT OR REPLACE INTO TemplatePageSectionQuestionTable(id, sectionId, questionText, guidelines, required, orderPosition, answerType, failedOptionId, neutralOptionId)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 9) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, sectionId)
          bindString(parameterIndex++, questionText)
          bindString(parameterIndex++, guidelines)
          bindBoolean(parameterIndex++, required)
          bindLong(parameterIndex++, orderPosition)
          bindString(parameterIndex++, answerType)
          bindString(parameterIndex++, failedOptionId)
          bindString(parameterIndex++, neutralOptionId)
        }
    notifyQueries(362_011_045) { emit ->
      emit("TemplatePageSectionQuestionTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteTemplatePageSectionQuestionsBySectionId(sectionId: String): QueryResult<Long> {
    val result = driver.execute(1_922_596_367, """DELETE FROM TemplatePageSectionQuestionTable WHERE sectionId = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, sectionId)
        }
    notifyQueries(1_922_596_367) { emit ->
      emit("TemplatePageSectionQuestionTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteTemplatePageSectionQuestionById(id: String): QueryResult<Long> {
    val result = driver.execute(-540_984_021, """DELETE FROM TemplatePageSectionQuestionTable WHERE id = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
        }
    notifyQueries(-540_984_021) { emit ->
      emit("TemplatePageSectionQuestionTable")
    }
    return result
  }

  private inner class GetTemplatePageSectionQuestionsBySectionIdQuery<out T : Any>(
    public val sectionId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("TemplatePageSectionQuestionTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("TemplatePageSectionQuestionTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_065_869_440, """SELECT TemplatePageSectionQuestionTable.id, TemplatePageSectionQuestionTable.sectionId, TemplatePageSectionQuestionTable.questionText, TemplatePageSectionQuestionTable.guidelines, TemplatePageSectionQuestionTable.required, TemplatePageSectionQuestionTable.orderPosition, TemplatePageSectionQuestionTable.answerType, TemplatePageSectionQuestionTable.failedOptionId, TemplatePageSectionQuestionTable.neutralOptionId FROM TemplatePageSectionQuestionTable WHERE sectionId = ? ORDER BY orderPosition ASC""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, sectionId)
    }

    override fun toString(): String = "TemplatePageSectionQuestionTable.sq:getTemplatePageSectionQuestionsBySectionId"
  }

  private inner class GetTemplatePageSectionQuestionByIdQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("TemplatePageSectionQuestionTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("TemplatePageSectionQuestionTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_725_046_884, """SELECT TemplatePageSectionQuestionTable.id, TemplatePageSectionQuestionTable.sectionId, TemplatePageSectionQuestionTable.questionText, TemplatePageSectionQuestionTable.guidelines, TemplatePageSectionQuestionTable.required, TemplatePageSectionQuestionTable.orderPosition, TemplatePageSectionQuestionTable.answerType, TemplatePageSectionQuestionTable.failedOptionId, TemplatePageSectionQuestionTable.neutralOptionId FROM TemplatePageSectionQuestionTable WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, id)
    }

    override fun toString(): String = "TemplatePageSectionQuestionTable.sq:getTemplatePageSectionQuestionById"
  }

  private inner class GetTemplatePageSectionQuestionCountBySectionIdQuery<out T : Any>(
    public val sectionId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("TemplatePageSectionQuestionTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("TemplatePageSectionQuestionTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-543_886_204, """SELECT COUNT(*) FROM TemplatePageSectionQuestionTable WHERE sectionId = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, sectionId)
    }

    override fun toString(): String = "TemplatePageSectionQuestionTable.sq:getTemplatePageSectionQuestionCountBySectionId"
  }
}
